/**
 * Manages UI state for the left navigation feature.
 * Keeps navigation mode, selection, flyout, and tree expansion state in one simple service.
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LeftNavState } from '../models/left-nav-state.model';

const INITIAL_LEFT_NAV_STATE: LeftNavState = {
    mode: 'collapsed',
    activeModuleId: null,
    activeItemId: null,
    openedFlyoutModuleId: null,
    expandedModuleId: null,
};

@Injectable()
export class LeftNavStore {
    private readonly stateSubject = new BehaviorSubject<LeftNavState>(INITIAL_LEFT_NAV_STATE);

    readonly state$: Observable<LeftNavState> = this.stateSubject.asObservable();

    get state(): LeftNavState {
        return this.stateSubject.value;
    }

    toggleNavigationMode(): void {
        const nextMode = this.state.mode === 'collapsed' ? 'full-tree' : 'collapsed';

        this.patchState({
            mode: nextMode,
            openedFlyoutModuleId: nextMode === 'full-tree' ? null : this.state.openedFlyoutModuleId,
        });
    }

    openFlyout(moduleId: string): void {
        this.patchState({ openedFlyoutModuleId: moduleId });
    }

    closeFlyout(): void {
        this.patchState({ openedFlyoutModuleId: null });
    }

    toggleTreeModule(moduleId: string): void {
        this.patchState({
            expandedModuleId: this.state.expandedModuleId === moduleId ? null : moduleId,
        });
    }

    setExpandedTreeModule(moduleId: string | null): void {
        this.patchState({ expandedModuleId: moduleId });
    }

    setActiveItem(moduleId: string, itemId: string | null = null): void {
        this.patchState({
            activeModuleId: moduleId,
            activeItemId: itemId,
        });
    }

    collapseAll(): void {
        this.patchState({
            openedFlyoutModuleId: null,
            expandedModuleId: null,
        });
    }

    private patchState(partial: Partial<LeftNavState>): void {
        this.stateSubject.next({
            ...this.state,
            ...partial,
        });
    }
}
